import { postAPI } from "../../../../api/ApiHelpers.ts";
import { store } from "../../../../redux/store.ts";

// Seat-based (billing_version 2) companies use the company-level billing
// endpoints; v1 companies keep the sourcing-module endpoints. Same function
// names and signatures, so every call site works for both unchanged.
const isSeatBased = () => store.getState()?.user?.user?.billing_version === 2;

export const createSourcingSubscription = async (
  slug: string,
  plan: string,
  seats?: number
) => {
  const url = isSeatBased()
    ? "billing/create-subscription"
    : "billing/sourcing/create-subscription";
  const payload: Record<string, unknown> = { plan };
  if (isSeatBased() && seats) payload.seats = seats;
  const response = await postAPI(url, payload, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(response?.data?.error || "Failed to create subscription");
};

export const cancelSourcingSubscription = async (slug: string) => {
  const url = isSeatBased()
    ? "billing/cancel-subscription"
    : "billing/sourcing/cancel-subscription";
  const response = await postAPI(url, {}, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(response?.data?.error || "Failed to cancel subscription");
};

export const confirmSourcingSubscription = async (
  slug: string,
  payload: {
    plan: string;
    seats?: number;
    razorpay_order_id?: string;
    razorpay_payment_id: string;
    razorpay_subscription_id?: string;
    razorpay_signature: string;
  }
) => {
  const url = isSeatBased()
    ? "billing/confirm-subscription"
    : "billing/sourcing/confirm-subscription";
  const response = await postAPI(url, payload, slug, {}, true);
  if (response?.status) {
    if (response.data?.status === "pending") {
      throw new Error("payment_pending");
    }
    return response?.data;
  }
  throw new Error(response?.data?.error || "Failed to confirm subscription");
};

// Platform (billing_version 2) only: seat count / plan changes on the live
// subscription. Server-side Razorpay update, prorated — no checkout step.
export const updatePlatformSeats = async (slug: string, seats: number) => {
  const response = await postAPI("billing/update-seats", { seats }, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(
    response?.data?.message || response?.data?.error || "Failed to update seats");
};

export const upgradePlatformPlan = async (slug: string, seats: number) => {
  const response = await postAPI("billing/upgrade-plan", { seats }, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(
    response?.data?.message || response?.data?.error || "Failed to upgrade plan");
};

// Staff recovery hatch (v2): pull live subscription state from Razorpay and
// sync status / period / seats — for when a webhook never arrived.
export const reconcilePlatformSubscription = async (slug: string) => {
  const response = await postAPI("billing/reconcile", {}, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(response?.data?.error || "Failed to reconcile");
};

// Staff: remove part (or all) of a company's remaining credits, with a note.
export const expireCompanyCredits = async (
  slug: string,
  payload: { credits: number; message: string }
) => {
  const response = await postAPI("billing/expire-credits", payload, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(response?.data?.error || "Failed to remove credits");
};

export const buySourcingCredits = async (
  slug: string,
  payload: { amount_rupees: number; tokens: number }
) => {
  const url = isSeatBased() ? "billing/buy-credits" : "billing/sourcing/buy-credits";
  const response = await postAPI(url, payload, slug, {}, true);
  if (response?.status) return response?.data;
  throw new Error(response?.data?.error || "Failed to create order");
};

export const confirmBuyCredits = async (
  slug: string,
  payload: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }
) => {
  const url = isSeatBased()
    ? "billing/confirm-buy-credits"
    : "billing/sourcing/confirm-buy-credits";
  const response = await postAPI(url, payload, slug, {}, true);
  if (response?.status) {
    if (response.data?.status === "pending") {
      throw new Error("payment_pending");
    }
    return response?.data;
  }
  throw new Error(response?.data?.error || "Failed to confirm payment");
};
